import { BANNER_WIDGET_ITEM_LINK_TYPE } from '@amondz/constants';
import dayjs from 'dayjs';

import { getRandomNumber } from '../../../../../lib/common';
import {
  CategoryBannerAttachmentType,
  CategoryBannerResponse,
  CategoryBannerSlideAttachmentResponse,
  UpsertCategoryBannerRequest,
} from '../../../../../lib/api/category/categoryBannerType';
import { CategoryBannerSlideFormItem, SlideImage } from '../types';
import {
  ATTACHMENT_TYPE,
  CATEGORY_BANNER_LINK_NUMBER_TO_TEXT,
  CATEGORY_BANNER_LINK_TEXT_TO_NUMBER,
  DATE_FORMAT,
  LINK_ID_REQUIRED_TYPES,
  LINK_URL_REQUIRED_TYPES,
} from '../constants';

/**
 * 슬라이드 필수 필드 누락 여부. antd compatible(v3) FormWithTab.validateTab은 form.validateFields의
 * callback 결과를 동기적으로 수집해 race condition이 발생하므로(검증 실패에도 onFinish가 호출되는 케이스),
 * 백엔드 호출 직전에 한 번 더 명시적으로 차단한다. UI 검증 메시지는 antd가 그대로 노출한다.
 */
export const isSlideIncomplete = (slide: CategoryBannerSlideFormItem): boolean => {
  if (!slide.mobileImage?.path) return true;
  if (!slide.pcImage?.path) return true;
  if (!slide.exposurePeriod.startDate) return true;
  if (
    slide.exposurePeriod.endDate &&
    slide.exposurePeriod.startDate >= slide.exposurePeriod.endDate
  ) return true;
  if (slide.isNonLink) return false;
  if (LINK_ID_REQUIRED_TYPES.includes(slide.linkType) && !slide.link?.id) return true;
  if (LINK_URL_REQUIRED_TYPES.includes(slide.linkType) && !slide.linkUrl) return true;
  return false;
};

const findAttachment = (
  attachments: CategoryBannerSlideAttachmentResponse[],
  type: CategoryBannerAttachmentType,
): SlideImage | null => {
  const found = attachments.find((a) => a.type === type);
  if (!found) return null;
  return { url: found.imageUrl, path: found.imagePath, width: found.width, height: found.height };
};

export const responseToFormItems = (data: CategoryBannerResponse): CategoryBannerSlideFormItem[] =>
  data.map((slide) => {
    const isNonLink = slide.linkType == null;
    return {
      // 서버의 slide.id가 아닌 getRandomNumber()를 쓰는 이유: FormListTab이 "+탭 추가" 시에도
      // 같은 getRandomNumber()로 id를 생성하므로, 서버 id를 유지하면 새 탭과 충돌할 수 있다.
      id: getRandomNumber(),
      mobileImage: findAttachment(slide.attachments, ATTACHMENT_TYPE.MOBILE),
      pcImage: findAttachment(slide.attachments, ATTACHMENT_TYPE.PC),
      isNonLink,
      // linkType이 null인 경우에도 Select 기본값은 유지해, 체크 해제 시 즉시 선택된 상태가 되도록 함.
      linkType: slide.linkType != null
        ? CATEGORY_BANNER_LINK_TEXT_TO_NUMBER[slide.linkType]
        : BANNER_WIDGET_ITEM_LINK_TYPE.EVENT,
      // 링크 없음 슬라이드는 서버 응답의 linkId/linkUrl도 버리고 비워둔다(비정상 응답 방어).
      // 백엔드 응답에 linkName이 포함되지 않아 ID로 대체 표시. 사용자가 재선택 시 실제 name이 주입됨.
      link: !isNonLink && slide.linkId != null ? { id: slide.linkId, name: `ID: ${slide.linkId}` } : null,
      linkUrl: !isNonLink ? slide.linkUrl ?? '' : '',
      // 서버 응답은 ISO 문자열이지만 DatePicker가 편집하면 DATE_FORMAT('YYYY-MM-DD HH:mm:ss')로 바뀐다.
      // 두 포맷이 섞이면 formRules.ts의 사전식 비교가 깨지므로 ('T' 0x54 > ' ' 0x20) 로드 시점에 정규화.
      exposurePeriod: {
        startDate: dayjs(slide.startDate).format(DATE_FORMAT),
        endDate: slide.endDate ? dayjs(slide.endDate).format(DATE_FORMAT) : null,
      },
    };
  });

export const formItemsToRequest = (items: CategoryBannerSlideFormItem[]): UpsertCategoryBannerRequest =>
  items.map((item) => {
    const slide: UpsertCategoryBannerRequest[number] = {
      attachments: [
        {
          type: ATTACHMENT_TYPE.MOBILE,
          imagePath: item.mobileImage?.path ?? '',
          width: item.mobileImage?.width ?? null,
          height: item.mobileImage?.height ?? null,
        },
        {
          type: ATTACHMENT_TYPE.PC,
          imagePath: item.pcImage?.path ?? '',
          width: item.pcImage?.width ?? null,
          height: item.pcImage?.height ?? null,
        },
      ],
      // 폼 상의 날짜는 DATE_FORMAT('YYYY-MM-DD HH:mm:ss', local) — dayjs로 파싱해 ISO(UTC)로 전송.
      // `new Date(str)`는 공백 구분 포맷 파싱이 ECMAScript 표준상 implementation-defined이므로 피한다.
      // dayjs도 포맷 인자 없이 호출하면 Safari/WebView에서 Invalid Date가 되므로 customParseFormat의 strict 모드로 명시.
      startDate: dayjs(item.exposurePeriod.startDate, DATE_FORMAT, true).toISOString(),
      endDate: item.exposurePeriod.endDate
        ? dayjs(item.exposurePeriod.endDate, DATE_FORMAT, true).toISOString()
        : null,
    };

    // 링크 없음: linkType / linkId / linkUrl 모두 payload에서 제외
    if (item.isNonLink) {
      return slide;
    }

    // 서버는 linkType을 응답과 동일한 텍스트('event'/'product'/'web')로 받는다.
    slide.linkType = CATEGORY_BANNER_LINK_NUMBER_TO_TEXT[item.linkType];
    if (item.linkType === BANNER_WIDGET_ITEM_LINK_TYPE.EXTERNAL_LINK) {
      slide.linkUrl = item.linkUrl;
    } else if (item.link?.id != null) {
      slide.linkId = item.link.id;
    }

    return slide;
  });
